import { useEffect, useState, useLayoutEffect, useRef, useCallback } from 'react'
import { X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Portal } from '../portal'

interface BottomSheetProps {
  isOpen: boolean
  onClose: () => void
  title?: string
  children: React.ReactNode
  className?: string
}

const DISMISS_THRESHOLD = 100 // pixels to drag before dismissing

/**
 * Reusable bottom sheet component with iOS safe area handling built-in.
 * Use this for any bottom sheet UI on mobile to ensure content isn't cut off.
 */
export function BottomSheet({ isOpen, onClose, title, children, className }: BottomSheetProps) {
  const [isVisible, setIsVisible] = useState(false)
  const [isAnimating, setIsAnimating] = useState(false)
  const [dragY, setDragY] = useState(0)
  const [isDragging, setIsDragging] = useState(false)
  
  const sheetRef = useRef<HTMLDivElement>(null)
  const dragStartY = useRef(0)
  const contentRef = useRef<HTMLDivElement>(null)

  // Handle visibility - synchronous to ensure DOM is ready
  useLayoutEffect(() => {
    if (isOpen) {
      setIsVisible(true)
      setDragY(0)
    }
  }, [isOpen])

  // Handle animation - runs after paint with small delay for reliability
  useEffect(() => {
    if (isOpen && isVisible) {
      // Small timeout ensures the initial translate-y-full state is painted
      const timeout = setTimeout(() => setIsAnimating(true), 10)
      return () => clearTimeout(timeout)
    } else if (!isOpen) {
      setIsAnimating(false)
      const timeout = setTimeout(() => setIsVisible(false), 300)
      return () => clearTimeout(timeout)
    }
  }, [isOpen, isVisible])

  // Lock body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [isOpen])

  // Touch handlers for swipe to dismiss
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    // Only allow drag from handle area or when content is scrolled to top
    const target = e.target as HTMLElement
    const isHandle = target.closest('[data-handle]')
    const isScrolledToTop = contentRef.current ? contentRef.current.scrollTop === 0 : true
    
    if (isHandle || isScrolledToTop) {
      dragStartY.current = e.touches[0].clientY
      setIsDragging(true)
    }
  }, [])

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!isDragging) return
    
    const currentY = e.touches[0].clientY
    const diff = currentY - dragStartY.current
    
    // Only allow dragging down (positive diff)
    if (diff > 0) {
      setDragY(diff)
    }
  }, [isDragging])

  const handleTouchEnd = useCallback(() => {
    if (!isDragging) return
    
    setIsDragging(false)
    
    if (dragY > DISMISS_THRESHOLD) {
      // Dismiss the sheet
      onClose()
    } else {
      // Snap back
      setDragY(0)
    }
  }, [isDragging, dragY, onClose])

  if (!isVisible) return null

  return (
    <Portal>
      <div className="fixed inset-0 z-50 md:hidden">
        {/* Backdrop */}
        <div
          className={cn(
            "absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity duration-300",
            isAnimating ? "opacity-100" : "opacity-0"
          )}
          style={{ opacity: isAnimating ? Math.max(0, 1 - dragY / 300) : 0 }}
          onClick={onClose}
        />

        {/* Sheet */}
        <div
          ref={sheetRef}
          className={cn(
            "absolute inset-x-0 bottom-0 bg-[#0a0a0a] border-t border-white/[0.08] rounded-t-2xl max-h-[85vh] flex flex-col",
            "will-change-transform touch-none",
            !isDragging && "transition-transform duration-300 ease-out",
            !isAnimating && !isDragging && "translate-y-full",
            className
          )}
          style={{ 
            transform: isAnimating ? `translateY(${dragY}px)` : undefined 
          }}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {/* Handle bar - draggable area */}
          <div data-handle className="flex justify-center pt-3 pb-1 shrink-0 cursor-grab active:cursor-grabbing">
            <div className="w-10 h-1 rounded-full bg-white/[0.2]" />
          </div>

          {/* Header */}
          {title && (
            <div data-handle className="flex items-center justify-between px-5 py-3 border-b border-white/[0.08] shrink-0">
              <span className="text-base font-semibold text-white">{title}</span>
              <button
                onClick={onClose}
                className="p-1.5 rounded-lg text-[#888] hover:text-white hover:bg-white/[0.05] transition-colors"
                aria-label="Close"
              >
                <X className="size-5" />
              </button>
            </div>
          )}

          {/* Content - scrollable with iOS safe area padding */}
          <div 
            ref={contentRef}
            className="flex-1 min-h-0 overflow-y-auto overscroll-contain touch-pan-y"
            style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 20px)' }}
          >
            {children}
          </div>
        </div>
      </div>
    </Portal>
  )
}
